import { useRef, useState, useCallback, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";

function ScrollableScreenshot({ src, alt, showHint = true }) {
  const viewportRef = useRef(null);
  const [scrollMax, setScrollMax] = useState(0);
  const [hovered, setHovered] = useState(false);
  const prefersReduced = useReducedMotion();

  const imgRef = useRef(null);

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const img = imgRef.current;
    if (!viewport || !img?.naturalWidth) return;
    const scale = viewport.clientWidth / img.naturalWidth;
    const scaledHeight = img.naturalHeight * scale;
    setScrollMax(Math.max(0, scaledHeight - viewport.clientHeight));
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    return () => ro.disconnect();
  }, [measure]);

  const scrollDuration = Math.min(14, Math.max(5, scrollMax / 70));
  const canScroll = scrollMax > 24 && !prefersReduced;

  return (
    <div
      ref={viewportRef}
      style={{ position: "absolute", inset: 0, overflow: "hidden", background: "#0a0a0a" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <motion.img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        onLoad={measure}
        initial={false}
        animate={{ y: hovered && canScroll ? -scrollMax : 0 }}
        transition={{
          duration: hovered && canScroll ? scrollDuration : 0.85,
          ease: hovered && canScroll ? "linear" : [0.22, 1, 0.36, 1],
        }}
        style={{ display: "block", width: "100%", height: "auto", willChange: "transform" }}
      />

      {scrollMax > 24 && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: hovered ? 40 : 64,
            background: "linear-gradient(to top, rgba(10,10,10,0.94), transparent)",
            pointerEvents: "none",
            transition: "height 0.4s ease",
          }}
        />
      )}

      {showHint && canScroll && !hovered && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 8,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "rgba(124,196,216,0.55)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          scroll ↓
        </div>
      )}
    </div>
  );
}

export default function PortfolioPreview({ src, alt }) {
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <ScrollableScreenshot src={src} alt={alt} />
    </div>
  );
}